import { APP_CONFIG } from "./config.ts";
import { loadPlayerProfile, PLAYER_PROFILE_KEY, savePlayerProfile } from "./player-profile.ts";
import { loadState, saveState, type PuzzleProgress, type SavedState, type StorageLike } from "./storage.ts";

// The game moved from code.bzwrd.co.uk to codelet.uk. Browser storage and the ranked-play cookie
// are bound to the old address, so a one-time handoff carries them across: the old host's
// /transfer/ page packs saved progress, the player name and a short-lived claim code into the
// URL fragment of the new address, and the app imports them on arrival. Fragments never reach
// a server, so nothing is logged in transit.

export const LEGACY_ORIGIN = "https://code.bzwrd.co.uk";
export const TRANSFER_FLAG = "whats-the-code:transfer:2";
const STATE_KEY = "whats-the-code:v1";

export interface TransferPayload {
  v: 1;
  state: string | null;
  profile: string | null;
  code: string | null;
}

export function transferReturnPath(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "/";
  return value;
}

export function transferUrl(returnPath: string): string {
  return `${LEGACY_ORIGIN}/transfer/?return=${encodeURIComponent(transferReturnPath(returnPath))}`;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function fromBase64Url(text: string): Uint8Array {
  const padded = text.replaceAll("-", "+").replaceAll("_", "/") + "=".repeat((4 - (text.length % 4)) % 4);
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

async function pipe(bytes: Uint8Array, stream: GenericTransformStream): Promise<Uint8Array> {
  const response = new Response(new Blob([bytes as BlobPart]).stream().pipeThrough(stream));
  return new Uint8Array(await response.arrayBuffer());
}

/** Encodes a payload for the URL fragment: "z." + gzip when available, "j." + plain JSON otherwise. */
export async function encodeTransfer(payload: TransferPayload): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(payload));
  if (typeof CompressionStream === "function") {
    return `z.${toBase64Url(await pipe(json, new CompressionStream("gzip")))}`;
  }
  return `j.${toBase64Url(json)}`;
}

export async function decodeTransfer(fragment: string): Promise<TransferPayload | undefined> {
  try {
    const [kind, data] = fragment.split(".", 2);
    if (!data) return undefined;
    let bytes = fromBase64Url(data);
    if (kind === "z") {
      if (typeof DecompressionStream !== "function") return undefined;
      bytes = await pipe(bytes, new DecompressionStream("gzip"));
    } else if (kind !== "j") {
      return undefined;
    }
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!parsed || typeof parsed !== "object" || (parsed as TransferPayload).v !== 1) return undefined;
    const payload = parsed as TransferPayload;
    return {
      v: 1,
      state: typeof payload.state === "string" ? payload.state : null,
      profile: typeof payload.profile === "string" ? payload.profile : null,
      code: typeof payload.code === "string" && /^[a-f0-9]{64}$/.test(payload.code) ? payload.code : null,
    };
  } catch {
    return undefined;
  }
}

const RANK: Record<PuzzleProgress["status"], number> = { ready: 0, playing: 1, failed: 2, solved: 3 };

/** Keeps whichever record of each day got further; ties go to what this browser already has. */
export function mergeSavedState(local: SavedState, imported: SavedState): SavedState {
  const progress = { ...local.progress };
  for (const [date, candidate] of Object.entries(imported.progress)) {
    const current = progress[date];
    const further = !current || RANK[candidate.status] > RANK[current.status] ||
      (candidate.status === current.status && candidate.attempts.length > current.attempts.length);
    if (further) progress[date] = candidate;
  }
  return { version: 1, progress };
}

function memoryStorage(values: Record<string, string | null>): StorageLike {
  return { getItem: (key) => values[key] ?? null, setItem: () => undefined };
}

export interface TransferImport {
  days: number;
  name: string;
  code: string | null;
}

/** Applies a payload to this browser's storage. Returns what was imported. */
export function importTransfer(storage: StorageLike, payload: TransferPayload): TransferImport {
  const imported = loadState(memoryStorage({ [STATE_KEY]: payload.state }));
  const before = loadState(storage);
  const merged = mergeSavedState(before, imported);
  const days = Object.keys(merged.progress).filter((date) => merged.progress[date] !== before.progress[date]).length;
  if (days > 0) saveState(storage, merged);

  let name = "";
  const current = loadPlayerProfile(storage, before);
  if (!current.name && payload.profile) {
    const profile = loadPlayerProfile(memoryStorage({ [PLAYER_PROFILE_KEY]: payload.profile }));
    if (profile.name) {
      savePlayerProfile(storage, profile);
      name = profile.name;
    }
  }
  try { storage.setItem(TRANSFER_FLAG, "done"); } catch { /* storage may be unavailable */ }
  return { days, name, code: payload.code };
}

/** True when a first visit to the new address should hop through the old one to collect history. */
export function shouldCollectTransfer(storage: StorageLike, hostname: string): boolean {
  if (hostname !== new URL(APP_CONFIG.siteUrl).hostname) return false;
  try {
    if (storage.getItem(TRANSFER_FLAG)) return false;
    const played = Object.values(loadState(storage).progress).some((progress) => progress.status !== "ready");
    const hasHistory = played || Boolean(loadPlayerProfile(storage).name);
    storage.setItem(TRANSFER_FLAG, hasHistory ? "done" : "pending");
    return !hasHistory;
  } catch {
    return false;
  }
}
